import React, { useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import API from "../services/api";
import "./AdminWorktimeSummary.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";

const normalizeMinutes = (value) => {
  let n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;

  const MAX_MONTHLY_MINUTES = 24 * 60 * 31; // practical upper bound
  const MAX_MONTHLY_MS = MAX_MONTHLY_MINUTES * 60 * 1000;

  // if backend accidentally sends milliseconds for a monthly range, convert to minutes
  if (n > MAX_MONTHLY_MINUTES && n <= MAX_MONTHLY_MS) {
    n = Math.round(n / 60000);
  }

  // extremely large values are likely invalid timestamps (epoch etc.)
  if (n > MAX_MONTHLY_MINUTES) return 0;
  return Math.round(n);
};

const fmtMins = (mins) => {
  const safeMins = normalizeMinutes(mins);
  const h = Math.floor(safeMins / 60);
  const m = safeMins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const fmtMoney = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (!parts.length) return "NA";
  return parts.map((p) => p[0].toUpperCase()).join("");
};

const statusDotClass = (lateMinutes = 0, overtimeMinutes = 0) => {
  if (Number(lateMinutes) > 30) return "off";
  if (Number(overtimeMinutes) > 0) return "on";
  return "idle";
};

const toCsv = (rows) => {
  const header = [
    "Employee",
    "Mobile",
    "Base Salary",
    "Total Days",
    "Work Time",
    "Overtime",
    "OT Pay",
    "Late",
    "Late Penalty",
    "Final Payout",
  ];
  const lines = rows.map((r) => {
    const salary = Number(r.workProfile?.salaryMonthly || 0);
    const overtimePay = Number(r.totals?.overtimePay || 0);
    const latePenalty = Number(r.totals?.latePenalty || 0);
    const finalPay = salary + overtimePay - latePenalty;
    return [
      r.fullName || "",
      r.mobile || "",
      salary.toFixed(2),
      Number(r.totals?.totalDays || 0),
      fmtMins(r.totals?.workMinutes),
      fmtMins(r.totals?.overtimeMinutes),
      overtimePay.toFixed(2),
      fmtMins(r.totals?.lateMinutes),
      latePenalty.toFixed(2),
      finalPay.toFixed(2),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",");
  });
  return [header.join(","), ...lines].join("\n");
};

const AdminWorktimeSummary = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const openPayrollWhatsApp = async (row) => {
    const phone = row?.mobile || "";
    const finalPay =
      Number(row?.workProfile?.salaryMonthly || 0) +
      Number(row?.totals?.overtimePay || 0) -
      Number(row?.totals?.latePenalty || 0);
    if (!phone) {
      setMessage("Employee mobile not available for WhatsApp");
      return;
    }
    try {
      await sendPortalWhatsApp({
        to: phone,
        recipientName: row?.fullName || "Employee",
        message: `Hello ${row?.fullName || "Employee"}, payroll summary: final payout ${fmtMoney(finalPay)} for selected period.`,
        sourceModule: "worktime_summary",
        eventType: "manual_payroll_message",
        entityType: "User",
        entityId: row?.employeeId || "",
        isAdmin: true,
      });
      setMessage("WhatsApp sent");
    } catch (e) {
      setMessage(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await API.get("/admin/worktime-summary", { params: { from, to } });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setPage(1);
      if (!data.length) setMessage("No records found.");
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load worktime summary.");
    } finally {
      setLoading(false);
    }
  };

  const totalPayout = useMemo(() => {
    return rows.reduce((sum, r) => {
      const salary = Number(r.workProfile?.salaryMonthly || 0);
      const overtimePay = Number(r.totals?.overtimePay || 0);
      const latePenalty = Number(r.totals?.latePenalty || 0);
      return sum + salary + overtimePay - latePenalty;
    }, 0);
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleExportCsv = () => {
    if (!rows.length) return;
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `worktime_summary_${from || "all"}_${to || "all"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="pws-page">
      <header className="pws-header">
        <div className="pws-header-inner">
          <div className="pws-brand">
            <div className="pws-brand-icon">
              <span className="material-symbols-outlined">account_balance_wallet</span>
            </div>
            <div>
              <h1>Employee Worktime & Overtime Summary</h1>
              <p>Payroll Management Portal</p>
            </div>
          </div>
          <div className="pws-head-right">
            <button className="pws-icon-btn" type="button">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <div className="pws-divider" />
            <div className="pws-admin-meta">
              <div>
                <strong>Admin User</strong>
                <small>Super Admin</small>
              </div>
              <div className="pws-admin-avatar">AD</div>
            </div>
          </div>
        </div>
      </header>

      <main className="pws-main">
        <section className="pws-filter-card">
          <div className="pws-filter-row">
            <div className="pws-date-grid">
              <div className="pws-field">
                <label>From Date</label>
                <div className="pws-input-wrap">
                  <span className="material-symbols-outlined">calendar_today</span>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
              </div>
              <div className="pws-field">
                <label>To Date</label>
                <div className="pws-input-wrap">
                  <span className="material-symbols-outlined">event</span>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
              <div className="pws-field pws-fetch-wrap">
                <button className="pws-fetch-btn" onClick={fetchSummary} disabled={loading}>
                  <span className="material-symbols-outlined">refresh</span>
                  {loading ? "Loading..." : "Fetch Report"}
                </button>
              </div>
            </div>
            <div className="pws-sep" />
            <div className="pws-payout">
              <div className="pws-payout-icon">
                <span className="material-symbols-outlined">payments</span>
              </div>
              <div>
                <p>Total Est. Payout</p>
                <h2>{fmtMoney(totalPayout)}</h2>
              </div>
            </div>
          </div>
        </section>

        {message ? <Alert variant="info">{message}</Alert> : null}

        <section className="pws-table-shell">
          {loading ? (
            <div className="pws-loading">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <div className="pws-empty">No data available. Select dates and fetch report.</div>
          ) : (
            <>
              <div className="pws-table-wrap">
                <table className="pws-table">
                  <thead>
                    <tr>
                      <th className="sticky-col">Employee Details</th>
                      <th>Base Salary</th>
                      <th>Work Time</th>
                      <th>Overtime</th>
                      <th>Late Time</th>
                      <th className="right">OT Pay (+)</th>
                      <th className="right">Late Penalty (-)</th>
                      <th className="right final-col">Final Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((r, i) => {
                      const salary = Number(r.workProfile?.salaryMonthly || 0);
                      const overtimePay = Number(r.totals?.overtimePay || 0);
                      const latePenalty = Number(r.totals?.latePenalty || 0);
                      const finalPay = salary + overtimePay - latePenalty;
                      const overtimeMinutes = Number(r.totals?.overtimeMinutes || 0);
                      const lateMinutes = Number(r.totals?.lateMinutes || 0);
                      return (
                        <tr key={`${r.employeeId || r.mobile || i}`}>
                          <td className="sticky-col">
                            <div className="pws-emp">
                              <div className="pws-avatar-wrap">
                                <div className="pws-avatar">{initials(r.fullName || "N A")}</div>
                                <span className={`pws-dot ${statusDotClass(lateMinutes, overtimeMinutes)}`} />
                              </div>
                              <div>
                                <p>{r.fullName || "-"}</p>
                                <small>{r.mobile || "-"}</small>
                                <div>
                                  <button className="btn-ghost" type="button" onClick={() => openPayrollWhatsApp(r)}>
                                    <BsWhatsapp />
                                    <span>WhatsApp</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{fmtMoney(salary)}</td>
                          <td>
                            <div className="pws-time">
                              <span className="material-symbols-outlined">schedule</span>
                              <span>{fmtMins(r.totals?.workMinutes)}</span>
                            </div>
                          </td>
                          <td>
                            <div className={`pws-time ${overtimeMinutes > 0 ? "ot" : "zero"}`}>
                              <span className="material-symbols-outlined">more_time</span>
                              <span>{fmtMins(overtimeMinutes)}</span>
                            </div>
                          </td>
                          <td>
                            <div className={`pws-time ${lateMinutes > 0 ? "negative" : "zero"}`}>
                              <span className="material-symbols-outlined">hourglass_bottom</span>
                              <span>{fmtMins(lateMinutes)}</span>
                            </div>
                          </td>
                          <td className="right positive">{overtimePay > 0 ? `+ ${fmtMoney(overtimePay)}` : fmtMoney(0)}</td>
                          <td className="right negative">{latePenalty > 0 ? `- ${fmtMoney(latePenalty)}` : fmtMoney(0)}</td>
                          <td className="right final-col strong">{fmtMoney(finalPay)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="pws-table-foot">
                <p>
                  Showing {Math.min((safePage - 1) * pageSize + 1, rows.length)} to{" "}
                  {Math.min(safePage * pageSize, rows.length)} of {rows.length} employees
                </p>
                <div className="pws-pager">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  <span className="current">{safePage}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <section className="pws-actions">
          <button className="btn-ghost" onClick={handleExportCsv}>
            <span className="material-symbols-outlined">description</span>
            Export CSV
          </button>
          <button className="btn-ghost" onClick={() => window.print()}>
            <span className="material-symbols-outlined">picture_as_pdf</span>
            Download PDF
          </button>
          <button className="btn-primary">
            <span className="material-symbols-outlined">check_circle</span>
            Finalize & Approve Payroll
          </button>
        </section>
      </main>
    </div>
  );
};

export default AdminWorktimeSummary;
